// src/screens/AnalyticsScreen.js
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Screen from '../Screen';
import api from '../api';

import backIcon from '../assets/backspace-icon.png';
import overviewIcon from '../assets/overview-icon.png';
import performanceIcon from '../assets/performance-icon.png';
import growthIcon from '../assets/growth-icon.png';
import boostIcon from '../assets/boost-icon.png';
import graphsIcon from '../assets/graphs-icon.png';
import dashboardPreviewBanner from '../assets/dashboardpreview-banner.png';

import './AnalyticsScreen.css';

const ENTRY_KEY = 'bf_analytics_entry_from'; // 'home' | 'profile'

export default function AnalyticsScreen({ user }) {
  const navigate = useNavigate();
  const location = useLocation();

  const [loading, setLoading] = useState(true);
  const [blocked, setBlocked] = useState(null); // { planId, message }
  const [error, setError] = useState('');

  // ✅ Determine entry source ONCE and persist it, so going into tiles doesn't lose it.
  const entryFrom = useMemo(() => {
    const fromState = location.state?.from;

    // If caller explicitly sets from, store it.
    if (fromState === 'home' || fromState === 'profile') {
      try {
        sessionStorage.setItem(ENTRY_KEY, fromState);
      } catch {}
      return fromState;
    }

    // Otherwise, use last stored entry.
    try {
      const saved = sessionStorage.getItem(ENTRY_KEY);
      if (saved === 'home' || saved === 'profile') return saved;
    } catch {}

    // Fallback (safe default)
    return 'profile';
  }, [location.state]);

  useEffect(() => {
    let alive = true;

    const checkAccess = async () => {
      setLoading(true);
      setError('');
      setBlocked(null);

      try {
        await api.get('/analytics/me?range=7d');
        if (!alive) return;
      } catch (e) {
        if (!alive) return;

        const status = e?.response?.status;
        const payload = e?.response?.data;

        if (status === 403 && payload?.code === 'ANALYTICS_NOT_ALLOWED') {
          setBlocked({
            planId: payload?.planId || 'FREE',
            message:
              payload?.message ||
              'Analytics is available on Premium and Premium+ plans.',
          });
        } else if (status === 401) {
          setError('Please login again.');
        } else {
          setError(payload?.message || 'Failed to load dashboard.');
        }
      } finally {
        if (alive) setLoading(false);
      }
    };

    checkAccess();
    return () => {
      alive = false;
    };
  }, []);

  const tiles = [
    { key: 'overview', label: 'Overview', icon: overviewIcon, route: '/analytics/overview' },
    { key: 'performance', label: 'Performance', icon: performanceIcon, route: '/analytics/performance' },
    { key: 'growth', label: 'Growth', icon: growthIcon, route: '/analytics/growth' },
    { key: 'boost', label: 'Boost', icon: boostIcon, route: '/analytics/boost', disabled: false },
    { key: 'graphs', label: 'Graphs', icon: graphsIcon, route: '/analytics/graphs/engagement' },
  ];

  // ✅ Exit based on persisted entry source:
  // - from home -> back to home
  // - from profile (or unknown) -> back to profile
  const goBack = () => {
    if (entryFrom === 'home') {
      navigate('/home', { replace: true });
      return;
    }
    navigate('/profile', { replace: true });
  };

  const openTile = (route, disabled) => {
    if (disabled) return;
    // ✅ Pass entryFrom forward (optional but nice), so deep screens can also know if needed
    navigate(route, { state: { from: entryFrom } });
  };

  return (
    <Screen>
      <div className="analytics-dashboard-container">
        <div className="analytics-dashboard-topbar">
          <img
            src={backIcon}
            alt="Back"
            className="analytics-dashboard-back"
            onClick={goBack}
          />
          <h2>Dashboard</h2>
          <div className="analytics-dashboard-spacer" />
        </div>

        {loading && (
          <div className="analytics-dashboard-loader">
            <div className="analytics-dashboard-spinner" />
            <p>Loading…</p>
          </div>
        )}



{!loading && blocked && (
  <div className="analytics-dashboard-gate">
    {/* Preview banner (like X) */}
    <div className="analytics-dashboard-preview">
      <img
        src={dashboardPreviewBanner}
        alt="Analytics preview"
        className="analytics-dashboard-preview-img"
      />
      <div className="analytics-dashboard-preview-fade" />
    </div>

    {/* Gate card */}
    <div className="analytics-dashboard-card analytics-dashboard-blocked">
      <h3>Premium feature</h3>
      <p>{blocked.message}</p>

      <div className="analytics-dashboard-blocked-row">
        <span className="analytics-dashboard-pill">
          Your plan: {blocked.planId}
        </span>

        <button
          className="analytics-dashboard-upgrade"
          onClick={() => navigate('/plans')}
        >
          Upgrade
        </button>
      </div>
    </div>
  </div>
)}



        {!loading && !blocked && error && (
          <div className="analytics-dashboard-card analytics-dashboard-error">
            <h3>Couldn’t load</h3>
            <p>{error}</p>
            <button
              className="analytics-dashboard-upgrade"
              onClick={() => window.location.reload()}
            >
              Retry
            </button>
          </div>
        )}

        {!loading && !blocked && !error && (
          <div className="analytics-dashboard-scroll">
            <div className="analytics-dashboard-tiles-grid">
              {tiles.map((t) => (
                <div
                  key={t.key}
                  className={`analytics-dashboard-tile ${t.disabled ? 'disabled' : ''}`}
                  onClick={() => openTile(t.route, t.disabled)}
                >
                  <img
                    src={t.icon}
                    alt={t.label}
                    className="analytics-dashboard-tile-icon"
                  />
                  <div className="analytics-dashboard-tile-label">
                    {t.label}
                  </div>
                  {t.disabled && (
                    <div className="analytics-dashboard-soon">Soon</div>
                  )}
                </div>
              ))}
            </div>

            <div className="analytics-dashboard-footer">
              Logged in as <b>@{user?.username || '—'}</b>
            </div>
          </div>
        )}
      </div>
    </Screen>
  );
}
